import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { playerAPI, dashboardAPI } from '../../services/api';
import OpponentTeamSheetModal from '../OpponentTeamSheetModal';
import {
  FaChevronRight,
  FaChevronLeft,
  FaUsers,
  FaClipboardList,
  FaTrophy,
  FaTimesCircle,
  FaBullseye,
  FaChartLine,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaInfoCircle,
} from 'react-icons/fa';

/** Tournaments deep-link (overview / statistics / schedule). */
function buildTournamentTeamUrl(team, section = 'overview', extraParams = {}) {
  if (!team?.tenantId || !team?.teamId || !team.latestSeasonId || !team.campaignId) {
    return '/player/tournaments';
  }
  const viewMap = {
    overview: 'overview',
    stats: 'statistics',
    statistics: 'statistics',
    schedule: 'schedule',
  };
  const view = viewMap[section] || section;
  const params = new URLSearchParams({
    area: 'leagues',
    tenant_id: String(team.tenantId),
    campaign_id: String(team.campaignId),
    season_id: String(team.latestSeasonId),
    team_id: String(team.teamId),
    view,
  });
  Object.entries(extraParams).forEach(([k, v]) => {
    if (v != null && v !== '') params.set(k, String(v));
  });
  return `/player/tournaments?${params.toString()}`;
}

const BRAND = {
  blue: '#043663',
  red: '#D52B1E',
  redHover: '#b52217',
  gray: '#E5E7EB',
  green: '#059669',
};

function formatScheduleMonthDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
}

/** Stable local calendar date for featured match (prefer confirmed match_date). */
function getFeaturedMatchDisplayDate(fm) {
  const raw = fm?.courtDetails?.match_date || fm?.date;
  if (!raw) return null;
  let d;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw.trim())) {
    d = new Date(`${raw.trim().slice(0, 10)}T12:00:00`);
  } else {
    d = new Date(raw);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatFeaturedMatchTime(fm, dateObj) {
  const mt = fm?.courtDetails?.match_time;
  if (mt != null && String(mt).trim() !== '') {
    const match = String(mt).match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      const base = dateObj ? new Date(dateObj.getTime()) : new Date();
      base.setHours(parseInt(match[1], 10), parseInt(match[2], 10), 0, 0);
      return base.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }
  const gw = fm?.date ? new Date(fm.date) : null;
  if (gw && !Number.isNaN(gw.getTime())) {
    const h = gw.getHours();
    const min = gw.getMinutes();
    if (h !== 0 || min !== 0) {
      return gw.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }
  const md = fm?.matchDateTime;
  if (typeof md === 'string' && md.includes(',')) {
    const tail = md.split(',').pop()?.trim();
    if (tail && /^\d{1,2}:\d{2}/.test(tail)) return tail;
  }
  return null;
}

function buildFeaturedVenueLine(fm) {
  const cnRaw = fm?.courtDetails?.court_name;
  const locRaw = fm?.courtDetails?.court_location;
  const cn = cnRaw != null ? String(cnRaw).trim() : '';
  const loc = locRaw != null ? String(locRaw).trim() : '';
  const v = fm?.venue && fm.venue !== 'TBD' ? String(fm.venue).trim() : '';
  if (v && cn) return `${v} / ${cn}`;
  if (loc && cn) return `${loc} / ${cn}`;
  if (cn) return cn;
  if (v) return v;
  if (loc) return loc;
  return null;
}

/** Completed matches only, newest first; counts consecutive wins from the latest result. */
function consecutiveWinStreakFromMatches(matches, { scoreKey = 'score' } = {}) {
  const completed = (matches || [])
    .filter((m) => m?.status === 'completed' && m?.[scoreKey] != null && String(m[scoreKey]).trim() !== '')
    .slice()
    .sort((a, b) => {
      const ta = new Date(a.date || a.completedAt || 0).getTime();
      const tb = new Date(b.date || b.completedAt || 0).getTime();
      return tb - ta;
    });
  let streak = 0;
  for (const m of completed) {
    if (String(m[scoreKey]).includes('Won')) streak += 1;
    else break;
  }
  return streak;
}

/** League overview match row completion (normalized for API quirks). */
function isLeagueMatchCompleted(m) {
  return String(m?.status || '').toLowerCase() === 'completed';
}

function leagueMatchScheduleAscCompare(a, b) {
  const da = getFeaturedMatchDisplayDate(a);
  const db = getFeaturedMatchDisplayDate(b);
  const ta = da?.getTime() ?? new Date(a?.date || 0).getTime();
  const tb = db?.getTime() ?? new Date(b?.date || 0).getTime();
  return ta - tb;
}

/** Context-aware copy when the team schedule column has no rows (layout stays tall; this fills the space). */
function teamScheduleEmptyHint(team, t) {
  if (!team) return t('playerDashboardPage.scheduleHintSelectTeam');
  if (!team.latestSeasonId) {
    return t('playerDashboardPage.scheduleHintNoSeason');
  }
  if (team.teamCardStatus === 'disabled') {
    return (
      team.teamCardStatusMessage ||
      t('playerDashboardPage.scheduleHintDisabled')
    );
  }
  if (team.campaignCompleted) {
    return t('playerDashboardPage.scheduleHintCampaignDone');
  }
  if (team.latestSeasonRegistrationOpen && !team.latestSeasonActiveNow) {
    return t('playerDashboardPage.scheduleHintRegOpen');
  }
  if (team.latestSeasonActiveNow) {
    return t('playerDashboardPage.scheduleHintActiveNoRows');
  }
  return t('playerDashboardPage.scheduleHintDefault');
}
function ScheduleTeamLogo({ url, title, compact }) {
  const [failed, setFailed] = useState(false);
  return (
    <ScheduleLogoSlot title={title || ''} $compact={compact}>
      {url && !failed ? (
        <img src={url} alt="" onError={() => setFailed(true)} />
      ) : (
        <FaUsers aria-hidden />
      )}
    </ScheduleLogoSlot>
  );
}

function FeaturedScheduleTeamLogo({ url, title }) {
  const [failed, setFailed] = useState(false);
  return (
    <FeaturedLogoSlot title={title || ''}>
      {url && !failed ? (
        <img src={url} alt="" onError={() => setFailed(true)} />
      ) : (
        <FaUsers aria-hidden />
      )}
    </FeaturedLogoSlot>
  );
}
const TeamPerfHeroOuter = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 1rem;
`;

const TeamPerfHeroCard = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.35rem;
  max-width: 100%;
  box-sizing: border-box;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px solid ${BRAND.blue};
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(4, 54, 99, 0.08);
  vertical-align: top;
`;

const TeamPerfHeroLogo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${BRAND.blue};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TeamPerfHeroName = styled.h3`
  margin: 0;
  font-size: clamp(1.125rem, 3vw, 1.5rem);
  font-weight: 800;
  color: ${BRAND.blue};
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-align: center;
  word-break: break-word;
  min-width: 0;
`;

const SeasonOverviewLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${BRAND.blue};
  margin-bottom: 0.65rem;
`;

const OverviewCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0 0.5rem;
  margin-bottom: 1.1rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 520px) {
    gap: 0.4rem;
    padding: 0 0.35rem;
  }
`;

const OverviewStatCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  text-align: center;
  gap: 6px;
  padding: 7px 6px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const OverviewStatIcon = styled.div`
  width: clamp(36px, 8.5vw, 42px);
  height: clamp(36px, 8.5vw, 42px);
  margin: 0 auto;
  border-radius: 10px;
  background: rgba(4, 54, 99, 0.09);
  color: ${BRAND.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: clamp(18px, 4.2vw, 22px);
    height: clamp(18px, 4.2vw, 22px);
  }
`;

const OverviewStatText = styled.div`
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
`;

const OverviewStatValue = styled.div`
  font-size: clamp(1.125rem, 3.8vw, 1.4rem);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.05;
  letter-spacing: -0.03em;
`;

const OverviewStatLabel = styled.div`
  font-size: clamp(0.625rem, 2.2vw, 0.78rem);
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.035em;
  line-height: 1.2;
  max-width: 100%;
  padding: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FeaturedMatchShell = styled.div`
  border-radius: 16px;
  background: linear-gradient(165deg, #061f3a 0%, ${BRAND.blue} 55%, #032842 100%);
  padding: 0.65rem 0.7rem 0.75rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 6px 22px rgba(6, 31, 58, 0.28);
`;

const FeaturedMatchTitleBlock = styled.div`
  padding: 0.05rem 0.25rem 0.55rem;
`;

const FeaturedMatchTitleText = styled.div`
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
`;

const FeaturedMatchTitleUnderline = styled.div`
  width: 2.35rem;
  height: 3px;
  margin-top: 0.38rem;
  border-radius: 2px;
  background: ${BRAND.red};
`;

const FeaturedMatchInner = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 0.85rem 0.85rem 0.9rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
`;

const FeaturedMatchTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem 1rem;
`;

const FeaturedMatchDateStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
  flex-shrink: 0;
  min-width: 2.85rem;
`;

const FeaturedMatchMonth = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  color: ${BRAND.red};
  letter-spacing: 0.06em;
`;

const FeaturedMatchDay = styled.span`
  font-size: clamp(1.45rem, 3.5vw, 1.85rem);
  font-weight: 800;
  color: #111827;
  margin-top: 0.22rem;
`;

const FeaturedMatchVsGrow = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeaturedLogoSlot = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  background: #eef2f7;
  color: ${BRAND.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(4, 54, 99, 0.14);

  svg {
    width: 22px;
    height: 22px;
    opacity: 0.88;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FeaturedTeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  max-width: 42%;
  flex: 1;
`;

const FeaturedTeamName = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const OpponentTeamHitFeatured = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  flex: 1;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  &:not(:disabled):hover .opp-team-name {
    color: ${BRAND.blue};
    text-decoration: underline;
  }
`;

const FeaturedVsBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  background: #4b5563;
  padding: 0.28rem 0.55rem;
  border-radius: 8px;
  flex-shrink: 0;
`;

const FeaturedVsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem 0.65rem;
  flex-wrap: wrap;
  width: 100%;
`;

const FeaturedMatchDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 0.85rem 0 0.75rem;
`;

const FeaturedMetaColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.65rem 1.25rem;
  margin-bottom: 0.85rem;
  align-items: start;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedMetaLeftStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
`;

const FeaturedMetaRightStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
  justify-self: end;

  @media (max-width: 480px) {
    justify-self: stretch;
  }
`;

const FeaturedDetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4b5563;
  line-height: 1.35;
  min-width: 0;

  svg {
    flex-shrink: 0;
    width: 17px;
    height: 17px;
    margin-top: 2px;
    color: #9ca3af;
    opacity: 0.95;
  }
`;

const FeaturedDetailItemRight = styled(FeaturedDetailItem)`
  @media (min-width: 481px) {
    justify-content: flex-end;
    text-align: right;

    svg {
      margin-top: 3px;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const GridRow3 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const MegaHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1.25rem;
`;

const MegaTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
`;

const MegaHeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
`;

const FullSeasonLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${BRAND.red};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const MegaBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.15rem;
  min-height: 300px;
  align-items: stretch;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

const ScheduleColumnShell = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 880px) {
    min-height: 260px;
  }
`;

const ScheduleSkeletonRow = styled.div`
  height: 86px;
  border-radius: 12px;
  background: linear-gradient(110deg, #f3f4f6 8%, #eceff3 18%, #f3f4f6 33%);
  background-size: 200% 100%;
  margin-bottom: 0.55rem;
  opacity: 0.85;
`;

const PerfCol = styled.div`
  min-width: 0;
`;

const PerfSectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 0.85rem;
`;

const StreakPanel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  background: #fff;
`;

const StreakPanelTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`;

const StreakPanelValue = styled.span`
  color: ${BRAND.red};
  font-weight: 800;
`;

const NextUpStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const ScheduleCard = styled.button`
  width: 100%;
  text-align: left;
  border-radius: 12px;
  padding: 0.55rem 0.6rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  border: ${(p) => (p.$highlight ? `2px solid ${BRAND.red}` : `1px solid #e5e7eb`)};
  background: ${(p) => (p.$highlight ? '#fffafb' : '#fff')};
  box-shadow: ${(p) =>
    p.$highlight ? '0 2px 10px rgba(213, 43, 30, 0.12)' : '0 1px 3px rgba(15, 23, 42, 0.05)'};
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${BRAND.blue};
    background: #fafbff;
    box-shadow: 0 3px 12px rgba(15, 23, 42, 0.07);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const ScheduleDateChip = styled.div`
  flex-shrink: 0;
  width: 2.75rem;
  padding: 0.32rem 0.22rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.08;
`;

const ScheduleChipMonth = styled.div`
  font-size: 0.56rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => (p.$highlight ? BRAND.red : BRAND.red)};
`;

const ScheduleChipDay = styled.div`
  font-size: 0.92rem;
  font-weight: 800;
  color: ${(p) => (p.$highlight ? BRAND.red : '#0f172a')};
  margin-top: 0.12rem;
`;

const ScheduleRowMiddle = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.25rem 0.35rem;
`;

const ScheduleVsCenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.18rem;
  flex-shrink: 0;
  padding: 0 0.15rem;
`;

const ScheduleListVsPill = styled.span`
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 0.2rem 0.42rem;
  border-radius: 999px;
`;

const ScheduleWindowHint = styled.div`
  font-size: 0.58rem;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
  max-width: 4.5rem;
  line-height: 1.2;
`;

const ScheduleListTeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.12rem;
  min-width: 0;
  flex: 1;
  max-width: 36%;
`;

const ScheduleListTeamName = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const OpponentTeamHitSchedule = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.12rem;
  min-width: 0;
  flex: 1;
  max-width: 100%;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  &:not(:disabled):hover .opp-team-name {
    color: ${BRAND.blue};
    text-decoration: underline;
  }
`;

const ScheduleMetaCol = styled.div`
  flex: 0 0 auto;
  text-align: right;
  min-width: 4.75rem;
  max-width: 7.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  justify-content: center;
`;

const ScheduleMetaPrimary = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${(p) => (p.$highlight ? BRAND.red : '#334155')};
  line-height: 1.2;
`;

const ScheduleMetaSecondary = styled.div`
  font-size: 0.58rem;
  font-weight: 500;
  color: #64748b;
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ScheduleListFootnote = styled.p`
  font-size: 0.68rem;
  color: #94a3b8;
  margin: 0.55rem 0 0;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  line-height: 1.35;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    opacity: 0.85;
  }
`;

const ScheduleDateCol = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  color: ${BRAND.red};
  line-height: 1.15;
  text-transform: uppercase;
  min-width: 2.85rem;
  max-width: 3.1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
`;

const ScheduleFlexMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  justify-content: center;
`;

const ScheduleVsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem 0.65rem;
  flex-wrap: wrap;
`;

const ScheduleTeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  min-width: 0;
  max-width: 42%;
  flex: 1;
`;

const ScheduleLogoSlot = styled.div`
  width: ${(p) => (p.$compact ? '38px' : '46px')};
  height: ${(p) => (p.$compact ? '38px' : '46px')};
  border-radius: 50%;
  overflow: hidden;
  background: rgba(4, 54, 99, 0.08);
  color: ${BRAND.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(4, 54, 99, 0.12);

  svg {
    width: ${(p) => (p.$compact ? '17px' : '20px')};
    height: ${(p) => (p.$compact ? '17px' : '20px')};
    opacity: 0.85;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ScheduleTeamName = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ScheduleVsLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  color: #9ca3af;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
  padding: 0 0.15rem;
`;

const ScheduleWhenMeta = styled.div`
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.35;
  text-align: center;
`;

const ScheduleAsideCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 0.35rem;
  flex-shrink: 0;
`;

const MatchNotifBadge = styled.span`
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 5px;
  border-radius: 999px;
  background: ${BRAND.red};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const PerformanceMegaCard = styled(Card)`
  padding: 1.35rem 1.5rem 1.5rem;
  margin-bottom: 1.25rem;
`;
const ProgressBar = styled.div`
  height: 10px;
  background: ${BRAND.gray};
  border-radius: 5px;
  overflow: hidden;
  margin: 0.75rem 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(p) => (p.$pct == null ? 0 : Math.min(100, Math.max(0, Number(p.$pct))))}%;
  background: ${BRAND.red};
  border-radius: 5px;
  transition: width 0.2s ease;
`;
const TeamNavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 0.5rem 0 0.75rem 0;
`;

const TeamNavBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: ${BRAND.blue};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: #f0f6fb;
  }
`;

const TeamNameCenter = styled.div`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: ${BRAND.blue};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TeamListLoading = styled.div`
  font-size: 14px;
  color: #6b7280;
  padding: 0.75rem 0;
`;

const EmptyHint = styled.div`
  font-size: 14px;
  color: #9ca3af;
  padding: 0.75rem 0;
`;
export default function PlayerLeaguePerformanceSchedule() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teamIndex, setTeamIndex] = useState(0);
  const [opponentSheetMatch, setOpponentSheetMatch] = useState(null);

  const { data: teamsData, isLoading: teamsLoading } = useQuery(
    ['playerTeams'],
    () => playerAPI.getTeams(),
    { enabled: !!user?.playerId, staleTime: 60 * 1000, refetchOnWindowFocus: true }
  );

  const myTeams = Array.isArray(teamsData?.data) ? teamsData.data : [];
  /** Single team: keep API order. Multiple teams: newest by teamCreatedAt first so default index 0 = latest. */
  const teamsOrderedForHome = useMemo(() => {
    if (myTeams.length <= 1) return myTeams;
    return [...myTeams].sort((a, b) => {
      const ta = a.teamCreatedAt ? new Date(a.teamCreatedAt).getTime() : 0;
      const tb = b.teamCreatedAt ? new Date(b.teamCreatedAt).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(b.teamId || '').localeCompare(String(a.teamId || ''));
    });
  }, [myTeams]);
  const tIdx = teamsOrderedForHome.length ? Math.min(teamIndex, teamsOrderedForHome.length - 1) : 0;
  const sel = teamsOrderedForHome[tIdx] || null;

  const openOpponentTeamSheetFromPlayer = useCallback(
    (match) => {
      if (!sel?.teamId || !match?.id || match?.opponentId == null) return;
      setOpponentSheetMatch(match);
    },
    [sel?.teamId]
  );

  const closeOpponentTeamSheetFromPlayer = useCallback(() => setOpponentSheetMatch(null), []);

  const { data: teamHomeOverview, isFetching: teamHomeFetching, isLoading: teamHomeLoading } = useQuery(
    ['playerHomeTeamOverview', sel?.teamId, sel?.latestSeasonId],
    () => {
      const h = sel.tenantId ? { headers: { 'X-Tenant-ID': sel.tenantId } } : {};
      return dashboardAPI.getOverview(sel.latestSeasonId, sel.teamId, h);
    },
    {
      enabled: !!user?.playerId && !!sel?.teamId && !!sel?.latestSeasonId,
      staleTime: 30 * 1000,
    }
  );

  const { data: notifUnreadSummaryRes } = useQuery(
    ['playerNotificationsUnreadSummary'],
    () => playerAPI.getNotificationsUnreadSummary(),
    {
      enabled: !!user?.playerId,
      staleTime: 20 * 1000,
      refetchInterval: 45 * 1000,
      refetchOnWindowFocus: true,
    }
  );
  const leagueNotifByMatchId = notifUnreadSummaryRes?.data?.byLeagueMatchId ?? {};

  const tov = teamHomeOverview?.data;
  const tTeamStats = tov?.teamStats || null;
  const tW = Number(tTeamStats?.wins ?? 0) || 0;
  const tL = Number(tTeamStats?.losses ?? 0) || 0;
  const tG = Number(tTeamStats?.games ?? tW + tL) || 0;
  const tWinRate = tG > 0 ? Math.round((100 * tW) / tG) : 0;
  const teamRecent = Array.isArray(tov?.recentMatches) ? tov.recentMatches : [];
  const teamRecentDisplay = teamRecent.slice(0, 100);
  /** Chronological fixtures in this snapshot (includes completed rows for status + parity with hero). */
  const teamScheduleMergedList = useMemo(() => {
    return [...teamRecentDisplay].sort(leagueMatchScheduleAscCompare).slice(0, 6);
  }, [teamRecentDisplay]);
  const featuredTeamMatch = useMemo(() => {
    const next = teamScheduleMergedList.find((m) => !isLeagueMatchCompleted(m));
    return next ?? null;
  }, [teamScheduleMergedList]);
  const allDisplayedMatchesCompleted =
    teamScheduleMergedList.length > 0 && teamScheduleMergedList.every(isLeagueMatchCompleted);
  const teamWinStreak = useMemo(
    () => consecutiveWinStreakFromMatches(teamRecentDisplay, { scoreKey: 'score' }),
    [teamRecentDisplay]
  );
  const streakBarPct = useMemo(() => {
    const cap = 12;
    const n = teamWinStreak;
    return Math.min(100, Math.round((100 * Math.min(n, cap)) / cap));
  }, [teamWinStreak]);

  const overviewStatCards = useMemo(() => {
    return [
      { Icon: FaClipboardList, label: t('playerDashboardPage.labelMatchesPlayed'), value: String(tG) },
      { Icon: FaTrophy, label: t('playerDashboardPage.labelWins'), value: String(tW) },
      { Icon: FaTimesCircle, label: t('playerDashboardPage.labelLosses'), value: String(tL) },
      { Icon: FaBullseye, label: t('playerDashboardPage.labelWinRate'), value: `${tWinRate}%` },
      { Icon: FaChartLine, label: t('playerDashboardPage.labelCurrentStreak'), value: String(teamWinStreak) },
    ];
  }, [tG, tW, tL, tWinRate, teamWinStreak, t]);


  if (teamsLoading) {
    return (
      <PerformanceMegaCard>
        <TeamListLoading>{t('playerDashboardPage.loadingDashboard')}</TeamListLoading>
      </PerformanceMegaCard>
    );
  }

  if (myTeams.length === 0) {
    return null;
  }

  return (
    <>
            <PerformanceMegaCard>
              <MegaHeader>
                <MegaTitle>{t('playerDashboardPage.megaTitle')}</MegaTitle>
                <MegaHeaderActions>
                  <FullSeasonLink
                    to={
                      sel && sel.tenantId && sel.latestSeasonId && sel.campaignId
                        ? buildTournamentTeamUrl(sel, 'statistics')
                        : '/player/tournaments?area=leagues'
                    }
                  >
                    {t('playerDashboardPage.fullSeasonStats')} <FaChevronRight size={11} aria-hidden />
                  </FullSeasonLink>
                </MegaHeaderActions>
              </MegaHeader>
      
              <>
                {teamsOrderedForHome.length > 1 && (
                  <TeamNavRow style={{ marginBottom: '0.75rem' }}>
                    <TeamNavBtn
                      type="button"
                      aria-label={t('playerDashboardPage.ariaPrevTeam')}
                      disabled={tIdx <= 0}
                      onClick={() => setTeamIndex((i) => Math.max(0, i - 1))}
                    >
                      <FaChevronLeft />
                    </TeamNavBtn>
                    <TeamNameCenter style={{ fontSize: 13 }} title={sel?.teamName || t('playerDashboardPage.teamFallback')}>
                      {t('playerDashboardPage.teamPager', {
                        current: tIdx + 1,
                        total: teamsOrderedForHome.length,
                      })}
                    </TeamNameCenter>
                    <TeamNavBtn
                      type="button"
                      aria-label={t('playerDashboardPage.ariaNextTeam')}
                      disabled={tIdx >= teamsOrderedForHome.length - 1}
                      onClick={() => setTeamIndex((i) => Math.min(teamsOrderedForHome.length - 1, i + 1))}
                    >
                      <FaChevronRight />
                    </TeamNavBtn>
                  </TeamNavRow>
                )}
                {sel ? (
                  <TeamPerfHeroOuter>
                    <TeamPerfHeroCard>
                      <TeamPerfHeroLogo>
                        {sel.teamLogoUrl ? (
                          <img src={sel.teamLogoUrl} alt="" />
                        ) : (
                          <FaUsers size={28} aria-hidden />
                        )}
                      </TeamPerfHeroLogo>
                      <TeamPerfHeroName title={sel.teamName || ''}>
                        {sel.teamName || t('playerDashboardPage.teamFallback')}
                      </TeamPerfHeroName>
                    </TeamPerfHeroCard>
                  </TeamPerfHeroOuter>
                ) : null}
      
              <SeasonOverviewLabel>
                {t('playerDashboardPage.seasonOverview')}
                {sel?.teamName ? ` · ${sel.teamName}` : ''}
              </SeasonOverviewLabel>
              <OverviewCardsGrid>
                {overviewStatCards.map(({ Icon, label, value }) => (
                  <OverviewStatCard key={label}>
                    <OverviewStatIcon>
                      <Icon aria-hidden />
                    </OverviewStatIcon>
                    <OverviewStatText>
                      <OverviewStatValue>{value}</OverviewStatValue>
                      <OverviewStatLabel>{label}</OverviewStatLabel>
                    </OverviewStatText>
                  </OverviewStatCard>
                ))}
              </OverviewCardsGrid>
      
              <MegaBody>
                <PerfCol>
                  {myTeams.length === 0 ? (
                      <EmptyHint>{t('playerDashboardPage.emptyJoinTeam')}</EmptyHint>
                    ) : (
                      <>
                        <PerfSectionLabel>
                          {(sel?.teamName || t('playerDashboardPage.teamFallback')).toUpperCase()}
                          {t('playerDashboardPage.momentumSuffix')}
                        </PerfSectionLabel>
                        {teamHomeLoading && (
                          <TeamListLoading>{t('playerDashboardPage.loadingTeamSnapshot')}</TeamListLoading>
                        )}
                        {!teamHomeLoading && !tov && (
                          <EmptyHint>{t('playerDashboardPage.emptyNoDashboardData')}</EmptyHint>
                        )}
                        {!teamHomeLoading && tov && (
                          <>
                            {featuredTeamMatch ? (
                              (() => {
                                const fm = featuredTeamMatch;
                              const displayDate = getFeaturedMatchDisplayDate(fm);
                              const monthShort = displayDate
                                ? displayDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
                                : null;
                              const dayNum = displayDate ? displayDate.getDate() : null;
                              const longDate =
                                displayDate &&
                                displayDate.toLocaleDateString('en-GB', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                });
                              const timeLine = formatFeaturedMatchTime(fm, displayDate);
                              const venueLine = buildFeaturedVenueLine(fm);
                              const homeName = fm.myTeamName || sel?.teamName || t('playerDashboardPage.teamFallback');
                              const awayName =
                                fm.opponentTeamName || fm.opponent || t('playerDashboardPage.opponentFallback');
                              const homeLogo = fm.myTeamLogoUrl || sel?.teamLogoUrl || null;
                              const awayLogo = fm.opponentTeamLogoUrl || null;
                              const datePrimary =
                                longDate ||
                                (typeof fm.matchDateTime === 'string' && fm.matchDateTime.trim()
                                  ? fm.matchDateTime.split(',')[0]?.trim()
                                  : null);
                              return (
                                <FeaturedMatchShell>
                                  <FeaturedMatchTitleBlock>
                                    <FeaturedMatchTitleText>{t('playerDashboardPage.nextMatch')}</FeaturedMatchTitleText>
                                    <FeaturedMatchTitleUnderline aria-hidden />
                                  </FeaturedMatchTitleBlock>
                                  <FeaturedMatchInner>
                                    <FeaturedMatchTopRow>
                                      <FeaturedMatchDateStack>
                                        <FeaturedMatchMonth>{monthShort || '—'}</FeaturedMatchMonth>
                                        <FeaturedMatchDay>{dayNum != null ? dayNum : '—'}</FeaturedMatchDay>
                                      </FeaturedMatchDateStack>
                                      <FeaturedMatchVsGrow>
                                        <FeaturedVsRow>
                                          <FeaturedTeamSide>
                                            <FeaturedScheduleTeamLogo url={homeLogo} title={homeName} />
                                            <FeaturedTeamName>{homeName}</FeaturedTeamName>
                                          </FeaturedTeamSide>
                                          <FeaturedVsBadge>{t('playerDashboardPage.vs')}</FeaturedVsBadge>
                                          <FeaturedTeamSide>
                                            {fm.opponentId != null ? (
                                              <OpponentTeamHitFeatured
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openOpponentTeamSheetFromPlayer(fm);
                                                }}
                                              >
                                                <FeaturedScheduleTeamLogo url={awayLogo} title={awayName} />
                                                <FeaturedTeamName className="opp-team-name">{awayName}</FeaturedTeamName>
                                              </OpponentTeamHitFeatured>
                                            ) : (
                                              <>
                                                <FeaturedScheduleTeamLogo url={awayLogo} title={awayName} />
                                                <FeaturedTeamName>{awayName}</FeaturedTeamName>
                                              </>
                                            )}
                                          </FeaturedTeamSide>
                                        </FeaturedVsRow>
                                      </FeaturedMatchVsGrow>
                                    </FeaturedMatchTopRow>
      
                                    <FeaturedMatchDivider />
      
                                    <FeaturedMetaColumns>
                                      <FeaturedMetaLeftStack>
                                        <FeaturedDetailItem>
                                          <FaCalendarAlt aria-hidden />
                                          <span>{datePrimary || '—'}</span>
                                        </FeaturedDetailItem>
                                        <FeaturedDetailItem>
                                          <FaMapMarkerAlt aria-hidden />
                                          <span>{venueLine || t('playerDashboardPage.venueTbd')}</span>
                                        </FeaturedDetailItem>
                                      </FeaturedMetaLeftStack>
                                      <FeaturedMetaRightStack>
                                        <FeaturedDetailItemRight>
                                          <FaClock aria-hidden />
                                          <span>{timeLine || '—'}</span>
                                        </FeaturedDetailItemRight>
                                      </FeaturedMetaRightStack>
                                    </FeaturedMetaColumns>
                                  </FeaturedMatchInner>
                                </FeaturedMatchShell>
                              );
                              })()
                            ) : allDisplayedMatchesCompleted ? (
                              <FeaturedMatchShell>
                                <FeaturedMatchTitleBlock>
                                  <FeaturedMatchTitleText>{t('playerDashboardPage.nextMatch')}</FeaturedMatchTitleText>
                                  <FeaturedMatchTitleUnderline aria-hidden />
                                </FeaturedMatchTitleBlock>
                                <FeaturedMatchInner>
                                  <div
                                    style={{
                                      padding: '0.95rem 0.65rem',
                                      textAlign: 'center',
                                      fontWeight: 600,
                                      fontSize: '0.9375rem',
                                      lineHeight: 1.55,
                                      color: '#4b5563',
                                    }}
                                  >
                                    {t('playerDashboardPage.allMatchesCompletedFeatured')}
                                  </div>
                                </FeaturedMatchInner>
                              </FeaturedMatchShell>
                            ) : null}
                            <StreakPanel>
                              <StreakPanelTop>
                                <span>{t('playerDashboardPage.currentWinStreak')}</span>
                                <StreakPanelValue>
                                  {teamWinStreak}{' '}
                                  {teamWinStreak === 1
                                    ? t('playerDashboardPage.streakMatch')
                                    : t('playerDashboardPage.streakMatches')}
                                </StreakPanelValue>
                              </StreakPanelTop>
                              <ProgressBar style={{ marginTop: 0 }}>
                                <ProgressFill $pct={streakBarPct} />
                              </ProgressBar>
                            </StreakPanel>
                          </>
                        )}
                      </>
                    )}
                </PerfCol>
      
                <PerfCol>
                  <ScheduleColumnShell>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                        flexWrap: 'wrap',
                        marginBottom: '0.85rem',
                      }}
                    >
                      <PerfSectionLabel style={{ marginBottom: 0 }}>
                        {t('playerDashboardPage.upcomingMatches')}
                      </PerfSectionLabel>
                      {sel && sel.tenantId && sel.latestSeasonId && sel.campaignId && (
                        <FullSeasonLink to={buildTournamentTeamUrl(sel, 'schedule')} style={{ fontSize: '0.8125rem' }}>
                          {t('playerDashboardPage.viewFullSchedule')} <FaChevronRight size={11} aria-hidden />
                        </FullSeasonLink>
                      )}
                    </div>
                    {myTeams.length === 0 ? (
                        <EmptyHint>{t('playerDashboardPage.emptyRosterFixtures')}</EmptyHint>
                      ) : teamHomeLoading ? (
                        <>
                          <TeamListLoading>{t('playerDashboardPage.loadingSchedule')}</TeamListLoading>
                          {[1, 2, 3, 4].map((k) => (
                            <ScheduleSkeletonRow key={k} aria-hidden />
                          ))}
                        </>
                      ) : (
                        <>
                          {teamHomeFetching && (
                            <TeamListLoading style={{ padding: '0 0 0.5rem' }}>{t('playerDashboardPage.updating')}</TeamListLoading>
                          )}
                          {teamScheduleMergedList.length === 0 ? (
                            <div
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem 0',
                              }}
                            >
                              <EmptyHint style={{ textAlign: 'center', lineHeight: 1.45 }}>
                                {teamScheduleEmptyHint(sel, t)}
                              </EmptyHint>
                            </div>
                          ) : (
                            <>
                              <NextUpStack>
                                {teamScheduleMergedList.map((m) => {
                                  const iso = m.courtDetails?.match_date || m.date;
                                  const dm = formatScheduleMonthDay(iso);
                                  const parts = dm.split(/\s+/).filter(Boolean);
                                  const completedRow = isLeagueMatchCompleted(m);
                                  const fid = featuredTeamMatch?.id;
                                  const mid = m?.id;
                                  const hl =
                                    !completedRow &&
                                    featuredTeamMatch &&
                                    fid != null &&
                                    mid != null &&
                                    String(fid) === String(mid);
                                  const displayDate = getFeaturedMatchDisplayDate(m);
                                  const exactTime = formatFeaturedMatchTime(m, displayDate);
                                  const metaTime =
                                    exactTime ||
                                    (typeof m.matchDateTime === 'string' && m.matchDateTime.includes(',')
                                      ? m.matchDateTime.split(',').pop()?.trim()
                                      : null) ||
                                    '—';
                                  const venueShort =
                                    buildFeaturedVenueLine(m) ||
                                    (m.venue && m.venue !== 'TBD' ? String(m.venue) : '');
                                  const completedSub = completedRow && m.score ? m.score : '';
                                  const primaryRight = completedRow
                                    ? completedSub || metaTime || '—'
                                    : metaTime;
                                  const secondaryRight = venueShort || '';
                                  const statusBadge = completedRow
                                    ? t('playerDashboardPage.scheduleMatchStatusCompleted')
                                    : String(m.status || '').toLowerCase() === 'scheduled'
                                      ? t('playerDashboardPage.scheduleMatchStatusScheduled')
                                      : '';
                                  const windowBelowVs =
                                    !completedRow && !exactTime && m.dateDisplay ? m.dateDisplay : '';
                                  const homeName = m.myTeamName || sel?.teamName || t('playerDashboardPage.teamFallback');
                                  const awayName =
                                    m.opponentTeamName || m.opponent || t('playerDashboardPage.opponentFallback');
                                  const homeLogo = m.myTeamLogoUrl || sel?.teamLogoUrl || null;
                                  const awayLogo = m.opponentTeamLogoUrl || null;
                                  const nLeague = leagueNotifByMatchId[String(m.id)] || 0;
                                  return (
                                    <ScheduleCard
                                      key={m.id || `${iso}-${m.opponent}`}
                                      type="button"
                                      $highlight={hl}
                                      onClick={() => sel && navigate(buildTournamentTeamUrl(sel, 'schedule'))}
                                    >
                                      <ScheduleDateChip>
                                        <ScheduleChipMonth $highlight={hl}>{parts[0] || '—'}</ScheduleChipMonth>
                                        <ScheduleChipDay $highlight={hl}>{parts[1] || '—'}</ScheduleChipDay>
                                      </ScheduleDateChip>
                                      <ScheduleRowMiddle>
                                        <ScheduleListTeamSide>
                                          <ScheduleTeamLogo url={homeLogo} title={homeName} compact />
                                          <ScheduleListTeamName>{homeName}</ScheduleListTeamName>
                                        </ScheduleListTeamSide>
                                        <ScheduleVsCenterColumn>
                                          <ScheduleListVsPill>{t('playerDashboardPage.vs')}</ScheduleListVsPill>
                                          {windowBelowVs ? (
                                            <ScheduleWindowHint>{windowBelowVs}</ScheduleWindowHint>
                                          ) : null}
                                        </ScheduleVsCenterColumn>
                                        <ScheduleListTeamSide>
                                          {m.opponentId != null ? (
                                            <OpponentTeamHitSchedule
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openOpponentTeamSheetFromPlayer(m);
                                              }}
                                            >
                                              <ScheduleTeamLogo url={awayLogo} title={awayName} compact />
                                              <ScheduleListTeamName className="opp-team-name">{awayName}</ScheduleListTeamName>
                                            </OpponentTeamHitSchedule>
                                          ) : (
                                            <>
                                              <ScheduleTeamLogo url={awayLogo} title={awayName} compact />
                                              <ScheduleListTeamName>{awayName}</ScheduleListTeamName>
                                            </>
                                          )}
                                        </ScheduleListTeamSide>
                                      </ScheduleRowMiddle>
                                      <ScheduleMetaCol>
                                        <ScheduleMetaPrimary $highlight={hl}>{primaryRight}</ScheduleMetaPrimary>
                                        {statusBadge ? (
                                          <div
                                            style={{
                                              marginTop: 3,
                                              fontSize: 10,
                                              fontWeight: 800,
                                              letterSpacing: '0.07em',
                                              textTransform: 'uppercase',
                                              color: completedRow ? BRAND.green : '#6b7280',
                                            }}
                                          >
                                            {statusBadge}
                                          </div>
                                        ) : null}
                                        {secondaryRight ? (
                                          <ScheduleMetaSecondary>{secondaryRight}</ScheduleMetaSecondary>
                                        ) : null}
                                      </ScheduleMetaCol>
                                      <ScheduleAsideCol>
                                        {nLeague > 0 ? (
                                          <MatchNotifBadge title={t('playerDashboardPage.matchNotifTitle')}>
                                            {nLeague > 99 ? '99+' : nLeague}
                                          </MatchNotifBadge>
                                        ) : null}
                                        <FaChevronRight style={{ color: '#9ca3af' }} size={14} aria-hidden />
                                      </ScheduleAsideCol>
                                    </ScheduleCard>
                                  );
                                })}
                              </NextUpStack>
                              <ScheduleListFootnote>
                                <FaInfoCircle aria-hidden size={12} />
                                <span>
                                  <Trans
                                    i18nKey="playerDashboardPage.scheduleFootnote"
                                    components={{ strong: <strong /> }}
                                  />
                                </span>
                              </ScheduleListFootnote>
                            </>
                          )}
                        </>
                      )}
                  </ScheduleColumnShell>
                </PerfCol>
              </MegaBody>
              </>
            </PerformanceMegaCard>

            {opponentSheetMatch && sel?.teamId && (
              <OpponentTeamSheetModal
                isOpen={!!opponentSheetMatch}
                onClose={closeOpponentTeamSheetFromPlayer}
                matchId={opponentSheetMatch.id}
                teamId={sel.teamId}
                matchForChat={opponentSheetMatch}
                onNavigateToChat={() => {
                  const m = opponentSheetMatch;
                  closeOpponentTeamSheetFromPlayer();
                  navigate(buildTournamentTeamUrl(sel, 'overview', { open_match_chat: m.id }));
                }}
              />
            )}
    </>
  );
}
